import moment from 'moment'
import DatePicker from 'react-datepicker'
import React, {useState, useMemo, useContext, useCallback} from 'react'
import {Frame, FrameHeader} from 'js/studios/shared/flows'
import {api} from 'js/studios/shared/api'
import {FlowFramesCtx} from 'js/studios/shared/unredux/contexts/flow'
import {KeyValueCtx} from 'js/studios/shared/unredux/contexts/key_value'
import {useOnMount} from 'js/studios/shared/unredux/hooks'
import {trackEventSP} from 'js/studios/shared/tracking'
import {classNames} from 'js/studios/shared/utils'
import {SHIPPING_COUNTRY_CHOICES} from './data'
import {AttachmentsProvider, AttachmentsContext, AttachmentsList, UploadButton} from 'js/studios/shared/unredux/attachments'
import {CheckMarkCard, HelpLink, HelpModal, BUDGET_OPTIONS} from 'js/studios/shared/flows'

export function resolveValueWithCards(value, cards) {
  if (value && cards.find(card => card.name === value)) {
    return value
  }
  return null
}

export function setCookie(key, value) {
  return api.post(window.cm.urls['cookies-api'], {key: key, value: value})
}

export function CalendarSelect({value, onChange}) {
  const DATE_FORMAT = "MM/DD/YY"
  const minDate = useMemo(() => moment().add(4, "weeks").toDate(), [])
  const handleChange = date => {
    onChange(date && moment(date).format(DATE_FORMAT))
  }
  const handleBlur = ev => {
    handleChange(moment(ev.target.value, DATE_FORMAT).toDate())
  }

  return <DatePicker
      selected={value && moment(value, DATE_FORMAT)}
      inline
      onChange={handleChange}
      onBlur={handleBlur}
      dateFormat={DATE_FORMAT}
      minDate={minDate}
    />
}

export function NextBtn({children}) {
  return <div className="btn-group anim--fade-in mt--lg">
    <button className="btn btn--lg btn--wide btn--arrow-right">{children || "Next"}</button>
  </div>
}

export function NotSure({className, field, fieldsToWipe, checked, children}) {
  const {setKey, resetKey} = useContext(KeyValueCtx)

  const setSkip = ev => {
    const skipped = ev.target.checked
    setKey(field, skipped)
    if (skipped && fieldsToWipe) {
      fieldsToWipe.forEach(resetKey)
    }
  }

  return <label className={className}>
    <input type="checkbox" checked={checked || false} onChange={setSkip} value="" />
    {children}
  </label>
}

export function BudgetFrame({setPreventionFrame}) {
  const {gotoNextFrame} = useContext(FlowFramesCtx)
  const {
    data: {flow, categoryTop, categorySecondary, budgetMin, budgetMax, budgetSkip},
    setKey,
  } = useContext(KeyValueCtx)

  const
    [helpOpen, setHelpOpen] = useState(false),
    isEngagement = flow === "engagement" || categoryTop === "engagement",
    isNecklace = categorySecondary === "necklace",
    updateBudget = (field, val) => {
      const valI = parseInt(val), budgetMinI = parseInt(budgetMin), budgetMaxI = parseInt(budgetMax)

      // if setting a field which contradicts the other field, they get swapped
      if (field === "budgetMax" && budgetMinI && valI && valI < budgetMinI) {
        setKey("budgetMin",  val)
        val = budgetMin
      } else if (field === "budgetMin" && budgetMaxI && valI && valI > budgetMaxI) {
        setKey("budgetMax", val)
        val = budgetMax
      }

      setKey(field, val)
    },
    setBudgetMin = ev => updateBudget("budgetMin", ev.target.value),
    setBudgetMax = ev => updateBudget("budgetMax", ev.target.value),
    openHelp = useCallback(ev => { ev.preventDefault(); setHelpOpen(true) }, []),
    closeHelp = useCallback(ev => { ev.preventDefault(); setHelpOpen(false) }, []),
    isLowBudget = () => {
      if (!budgetSkip && parseInt(budgetMax)) {
        if (isEngagement && parseInt(budgetMax) <= 500) {
          return true
        } else if (!isEngagement && parseInt(budgetMax) <= 200) {
          return true
        }
      }
      return false
    },
    onSubmit = ev => {
      ev.preventDefault()
      if (isLowBudget()) {
        trackEventSP('action_view_prevention_frame', 'consultation', {
          frame: 'prevention-low-budget',
          reason: `budget-max:${budgetMax}`,
        })
        setPreventionFrame("prevention-low-budget")
      } else {
        gotoNextFrame()
      }
    },
    canSubmit = budgetMin || budgetMax || budgetSkip

  const maxValue = `${BUDGET_OPTIONS[BUDGET_OPTIONS.length - 1]}+`
  const budgetOptions = [
    <option key="any" value="any">Any</option>,
    ...BUDGET_OPTIONS.map(opt => <option value={opt} key={opt}>${opt}</option>),
    <option key={maxValue} value={maxValue}>${maxValue}</option>,
  ]

  return <Frame canSubmit={canSubmit} onSubmit={onSubmit}>
    <FrameHeader>
      Set your budget.
      <HelpLink label="Open help about budget" onClick={openHelp} />
    </FrameHeader>
    <p className="txt--center mb--lg">If you have a specific price point in mind, let us know so we can help guide you towards options that fit your budget.</p>
    <fieldset className="field--lg flex-row mb--1">
      <select aria-label="Minimum budget" onChange={setBudgetMin} value={budgetMin} disabled={budgetSkip}>
        <option value="" disabled>Minimum</option>
        {budgetOptions}
      </select>
      <select aria-label="Maximum budget" onChange={setBudgetMax} value={budgetMax} disabled={budgetSkip}>
        <option value="" disabled>Maximum</option>
        {budgetOptions}
      </select>
    </fieldset>
    {!isNecklace &&
      <NotSure field="budgetSkip" checked={budgetSkip} fieldsToWipe={["budgetMin", "budgetMax"]}>I’d prefer not to say.</NotSure>
    }
    <HelpModal name="budget" isOpen={helpOpen} onClose={closeHelp}>
      <p>Instead of having pre-made jewelry with price tags on them, our goal is to create amazing jewelry that fits <i>your</i> budget.</p>
      {!isNecklace &&
        <p className="mb--0">If you don't know your price point, or aren't comfortable discussing it yet, that's okay too!</p>
      }
    </HelpModal>
  </Frame>
}

export function LowBudgetPreventionFrame() {
  const {data: {flow, categoryTop}} = useContext(KeyValueCtx)
  const isEngagement = flow === "engagement" || categoryTop === "engagement"
  const backToHomepage = useCallback(ev => {
    if (ev) { ev.preventDefault() }
    window.location.href = '/'
  }, [])

  useOnMount(() => {
    setCookie('prevention_frame', 'prevention-low-budget').catch(_err => null /* nop */)
  })

  return <Frame dark={true} canSubmit={true} cta="Back to homepage" onSubmit={backToHomepage}>
    <img src="//images.custommade.com/Fyqsyd-lFeUPiJqiYDs6B1i4m6s=/custommade-attachments/ab1a91d44dedcbe_custom_made_icon_diamond.png" alt="" role="presentation" />
    <h2 className="txt--center">We’re sorry.</h2>
    <p className="txt--center">While we do our best to make special {isEngagement ? 'engagement rings' : 'jewelry'} accessible to all budgets, your target budget is a bit below the lowest point we’re able to offer.</p>
  </Frame>
}

export function ShippingLocationFrame() {
  const {gotoNextFrame} = useContext(FlowFramesCtx)
  const {
    data: {categoryTop, sourceCategory, shippingLocation},
    boundSetter,
  } = useContext(KeyValueCtx)

  const
    projectNoun = useMemo(() => {
      if (categoryTop === 'engagement') {
        return 'ring'
      }

      switch (sourceCategory) {
        case 'signet_ring':
        case 'class_ring':
        case 'mens_wedding_band':
        case 'womens_wedding_band':
        case 'cocktail_ring':
        case 'other_ring':
          return 'ring'
        case 'wedding_band_set':
          return 'rings'
        case 'necklace':
          return 'necklace'
        case 'cufflinks':
          return 'cufflinks'
        case 'earrings':
          return 'earrings'
        default:
          return 'project'
      }
    }, [categoryTop, sourceCategory]),
    shippingLocationErr = useMemo(() => {
      if (shippingLocation && !window.cm.bootstrap.shippingAllowedCountries.includes(shippingLocation)) {
        return "Unfortunately, we don't currently ship to that location."
      }
    }, [shippingLocation]),
    canSubmit = useMemo(() => {
      if (!shippingLocation) {
        return false
      }
      if (shippingLocationErr) {
        trackEventSP('Invalid shipping location', 'consultation', {
          frame: 'shipping-location',
          reason: `invalid-shipping-location:${shippingLocation}`,
        })
        return false
      }
      return true
    }, [shippingLocationErr, shippingLocation])

  return <Frame canSubmit={canSubmit} onSubmit={gotoNextFrame} ctaErrMsg={!shippingLocationErr ? "Please select a shipping location to continue." : ''}>
    <FrameHeader>Where will we be shipping your {projectNoun}?</FrameHeader>
    <p className="txt--center mb--lg">
      We ship to 25+ countries worldwide. Where in the world will your {projectNoun} be delivered?
    </p>
    <select aria-label="Shipping location" className="field--lg" onChange={boundSetter("shippingLocation")} value={shippingLocation || ""}>
      <option value="" disabled>Choose a response</option>
      {SHIPPING_COUNTRY_CHOICES.map(choice => <option key={choice.value} value={choice.value}>{choice.text}</option>)}
    </select>
    {shippingLocationErr && <p className="flow-error">{shippingLocationErr}</p>}
  </Frame>
}

export function SourceFrame() {
  const {gotoNextFrame} = useContext(FlowFramesCtx)
  const {data: {source}, boundSetter} = useContext(KeyValueCtx)
  const canSubmit = !!source

  return <Frame canSubmit={canSubmit} onSubmit={gotoNextFrame}>
    <FrameHeader>How did you find us?</FrameHeader>
    <p className="txt--center mb--lg">Whether you stumbled across us on your own, or heard about us from a friend, letting us know how you found your way to us will help us out!</p>
    <select aria-label="How did you find us?" onChange={boundSetter("source")} value={source}>
      <option value="" disabled>Choose a response</option>
      <option value="my_significant_other">My significant other</option>
      <option value="friends_or_family">Friends or family</option>
      <option value="internet_search">Search (Google, Bing, etc.)</option>
      <option value="instagram">Instagram</option>
      <option value="tiktok">TikTok</option>
      <option value="youtube">YouTube</option>
      <option value="facebook">Facebook</option>
      <option value="reddit">Reddit</option>
      <option value="pinterest">Pinterest</option>
      <option value="ai">AI Suggestion (ChatGPT, Gemini, etc.)</option>
      <option value="other">Other (website, blog, podcast, etc.)</option>
    </select>
  </Frame>
}


/* ShareYourIdea is funky because of attachments and legacy cruft from redux. Under redux this was
 * split out so we could deal with attachments within the AttachmentsContext and then push into
 * redux store when moving to the next frame. Now that I'm refactoring away from redux this could
 * maybe be simplified but for now behaves similarly.
 */
function ShareYourIdeaBody() {
  const
    {gotoNextFrame} = useContext(FlowFramesCtx),
    {data: {details}, setKey, boundSetter} = useContext(KeyValueCtx),
    {attachments} = useContext(AttachmentsContext),
    detailsErr = useMemo(() => {
      const trimmedVal = details?.trim?.()
      if (trimmedVal && trimmedVal.length > 1_500) {
        return "Too long, please be a bit briefer."
      }
    }, [details]),
    attachmentErrMsg = useMemo(() => {
      const errAttachment = attachments.find(a => a.error)
      if (errAttachment) {
        return `${errAttachment.error}${errAttachment.error?.endsWith?.(".") ? "" : "."} Remove errored attachments to proceed.`
      }
    }, [attachments]),
    canSubmit = useMemo(() => {
      // details are required, and can't proceed with uploading/errored attachments
      return details?.trim?.() && !detailsErr && !attachments.some(a => a.isUpload || a.error)
    }, [details, detailsErr, attachments]),
    onSubmit = useCallback(ev => {
      ev.preventDefault()
      setKey("attachments", attachments)
      gotoNextFrame()
    }, [attachments, setKey, gotoNextFrame])

  return <Frame canSubmit={canSubmit} onSubmit={onSubmit}>
    <FrameHeader>Some details to share with us?</FrameHeader>
    <p className="txt--center mb--lg">Tell us (or show us) what you'd like to make, and we'll help you bring it to life!</p>
    <textarea className="field--lg mb--1" aria-label="Share your idea here" placeholder="Your idea here..." onChange={boundSetter("details")} value={details} rows="4"></textarea>
    {detailsErr && <p className="flow-error mb--1_5">{detailsErr}</p>}
    <UploadButton className="btn btn--secondary"><span className="icon icon--upload ml--0 mr--0_5"></span>Upload images</UploadButton>
    {attachmentErrMsg && <p className="flow-error">{attachmentErrMsg}</p>}
    {attachments?.length ? <AttachmentsList className="field--lg mt--2 mb--0" editable={true} /> : null}
  </Frame>
}

export function ShareYourIdea() {
  const {data: {attachments}} = useContext(KeyValueCtx)
  return <AttachmentsProvider attachments={attachments}><ShareYourIdeaBody /></AttachmentsProvider>
}

export function MetalColor({cards}) {
  const {gotoNextFrame} = useContext(FlowFramesCtx)
  const {
    data: {metalColor: metalColorRaw, metalColorSkip},
    boundSetter,
  } = useContext(KeyValueCtx)
  const
    metalColor = useMemo(() => resolveValueWithCards(metalColorRaw, cards), [metalColorRaw, cards]),
    isChecked = name => metalColor === name,
    canSubmit = metalColor || metalColorSkip

  return <Frame canSubmit={canSubmit} onSubmit={gotoNextFrame}>
    <FrameHeader h2ClassName="mb--lg">Choose a metal color.</FrameHeader>
    <div className="check-mark-cards-list check-mark-cards-list--3col mb--lg">
      {cards.map(card => <CheckMarkCard
        key={card.name}
        imgs={[card.img]}
        value={card.name}
        description={card.label}
        checked={isChecked(card.name)}
        disabled={metalColorSkip}
        onClick={boundSetter("metalColor")}
        />
      )}
    </div>
    <NotSure className="mb--lg" field="metalColorSkip" fieldsToWipe={['metalColor']} checked={metalColorSkip}>I’m not sure.</NotSure>
    <p className="info-note txt--caption">We work in castable precious metals only: sterling silver, gold, and platinum. Mixed-metal designs are subject to certain design limitations.</p>
  </Frame>
}

export function Finish({cards}) {
  const {gotoNextFrame} = useContext(FlowFramesCtx)
  const {data: {finish: finishRaw, finishSkip}, boundSetter} = useContext(KeyValueCtx)
  const
    finish = useMemo(() => resolveValueWithCards(finishRaw, cards), [finishRaw, cards]),
    isChecked = name => finish === name,
    canSubmit = finish || finishSkip

  return <Frame canSubmit={canSubmit} onSubmit={gotoNextFrame}>
    <FrameHeader h2ClassName="mb--lg">Choose a metal finish.</FrameHeader>
    <div className="check-mark-cards-list check-mark-cards-list--3col mb--lg">
      {cards.map(card => <CheckMarkCard
        key={card.name}
        imgs={[card.img]}
        value={card.name}
        description={card.label}
        checked={isChecked(card.name)}
        disabled={finishSkip}
        onClick={boundSetter("finish")}
        />
      )}
    </div>
    <NotSure className="mb--lg" field="finishSkip" fieldsToWipe={['finish']} checked={finishSkip}>I’m not sure.</NotSure>
    <p className="info-note txt--caption">A polished or brushed finish is the most durable; oxidized and enamel jewelry are subject to design limitations and require special care and maintenance.</p>
  </Frame>
}

export function Gemstones({subheader, cards}) {
  const {gotoNextFrame} = useContext(FlowFramesCtx)
  const {data: {gemstones: gemstonesRaw, gemstonesSkip}, boundSetter} = useContext(KeyValueCtx)
  const
    gemstones = useMemo(() => resolveValueWithCards(gemstonesRaw, cards), [gemstonesRaw, cards]),
    isChecked = name => gemstones === name,
    canSubmit = gemstones || gemstonesSkip

  return <Frame canSubmit={canSubmit} onSubmit={gotoNextFrame}>
    <FrameHeader>Choose gemstones.</FrameHeader>
    <p className="txt--center mb--lg">{subheader || "Are you considering any gemstones for your ring?"}</p>
    <div className="check-mark-cards-list check-mark-cards-list--3col mb--lg">
      {cards.map(card => <CheckMarkCard
        key={card.name}
        imgs={[card.img]}
        value={card.name}
        description={card.label}
        checked={isChecked(card.name)}
        disabled={gemstonesSkip}
        onClick={boundSetter("gemstones")}
        />
      )}
    </div>
    <NotSure field="gemstonesSkip" fieldsToWipe={['gemstones']} checked={gemstonesSkip}>I’m not sure.</NotSure>
  </Frame>
}

export function NextSteps({subheader, cards}) {
  const {gotoNextFrame} = useContext(FlowFramesCtx)
  const {data: {nextSteps: nextStepsRaw}, boundSetter} = useContext(KeyValueCtx)
  const
    nextSteps = useMemo(() => resolveValueWithCards(nextStepsRaw, cards), [nextStepsRaw, cards]),
    isChecked = name => nextSteps === name

  return <Frame canSubmit={nextSteps} onSubmit={gotoNextFrame}>
    <FrameHeader>Where shall we begin?</FrameHeader>
    <p className="txt--center mb--lg">{subheader || "We have all the information we need to get started! We can start by talking more about the design of your ring, or we can get right to some price estimates - which would you like to do first?"}</p>
    <div className="check-mark-cards-list mb--lg">
      {cards.map(card => <CheckMarkCard
        key={card.name}
        value={card.name}
        label={card.label}
        description={card.description}
        checked={isChecked(card.name)}
        onClick={boundSetter("nextSteps")}
      />)}
    </div>
  </Frame>
}

export function RingFaceShape({cards, subheader}) {
  const {gotoNextFrame} = useContext(FlowFramesCtx)
  const {
    data: {ringFaceShape: ringFaceShapeRaw, ringFaceShapeSkip},
    boundSetter,
  } = useContext(KeyValueCtx)

  const
    ringFaceShape = useMemo(() => resolveValueWithCards(ringFaceShapeRaw, cards), [ringFaceShapeRaw, cards]),
    isChecked = name => ringFaceShape === name,
    canSubmit = ringFaceShape || ringFaceShapeSkip

  return <Frame canSubmit={canSubmit} onSubmit={gotoNextFrame}>
    <FrameHeader>Choose a ring face shape.</FrameHeader>
    <p className="txt--center mb--lg">{subheader || "The portrait oval is our most popular choice, but there’s no wrong answer when it comes to face shape! Since our rings are fully custom, you aren't limited to the examples - we can create any shape you like."}</p>
    <div className="check-mark-cards-list check-mark-cards-list--3col mb--lg">
      {cards.map(card => <CheckMarkCard
        key={card.name}
        imgs={[card.img]}
        value={card.name}
        description={card.label}
        checked={isChecked(card.name)}
        disabled={ringFaceShapeSkip}
        onClick={boundSetter("ringFaceShape")}
      />)}
    </div>
    <NotSure field="ringFaceShapeSkip" fieldsToWipe={['ringFaceShape']} checked={ringFaceShapeSkip}>I’m not sure yet.</NotSure>
  </Frame>
}

export function RaisedOrRecessed({cards}) {
  const {gotoNextFrame} = useContext(FlowFramesCtx)
  const {
    data: {raisedOrRecessed: raisedOrRecessedRaw, raisedOrRecessedSkip},
    boundSetter,
  } = useContext(KeyValueCtx)

  const
    raisedOrRecessed = useMemo(() => resolveValueWithCards(raisedOrRecessedRaw, cards), [raisedOrRecessedRaw, cards]),
    isChecked = name => raisedOrRecessed === name,
    canSubmit = raisedOrRecessed || raisedOrRecessedSkip

  return <Frame canSubmit={canSubmit} onSubmit={gotoNextFrame}>
    <FrameHeader>Raised or recessed design?</FrameHeader>
    <p className="txt--center mb--lg">If you intend to use your ring to create a stamp or wax seal, we can mirror the image so that the resulting stamp will properly show the design and/or text. Otherwise, raised vs recessed is just a stylistic choice!</p>
    <div className="check-mark-cards-list mb--lg">
      {cards.map(card => <CheckMarkCard
        key={card.name}
        value={card.name}
        description={card.label}
        imgs={card.imgs}
        checked={isChecked(card.name)}
        disabled={raisedOrRecessedSkip}
        onClick={boundSetter("raisedOrRecessed")}
      />)}
    </div>
    <NotSure field="raisedOrRecessedSkip" fieldsToWipe={['raisedOrRecessed']} checked={raisedOrRecessedSkip}>I’m not sure yet.</NotSure>
  </Frame>
}

export function RingFaceSize({img}) {
  const HANDS = {
    female: "//images.custommade.com/w3fdULNIVkq6BZVzjQgjrfKtuwI=/custommade-attachments/0729cdfc7baffd9_female_hand.jpeg",
    male: "//images.custommade.com/-kalOTEjwJsbYg0PSP9eTrvpnY0=/custommade-attachments/24599f36dea9cba_male_hand.jpg",
  }

  const
    {gotoNextFrame} = useContext(FlowFramesCtx),
    {data: {ringFaceSize}, boundSetter} = useContext(KeyValueCtx),
    [gender, setGender] = useState("male"),
    otherGender = gender === "female" ? "male" : "female",
    toggleGender = ev => {
      ev.preventDefault()
      setGender(otherGender)
    }

  return <Frame canSubmit={ringFaceSize} onSubmit={gotoNextFrame}>
    <FrameHeader>Choose a ring face size.</FrameHeader>
    <p className="txt--center mb--lg">
      We usually recommend a 15mm ring face for a typical man's ring. If you're planning for this to
      be a pinky ring or for a woman, a 13mm ring face is a great fit. Bigger options (17mm+) make
      a statement and look good on large fingers.
    </p>
    <div className={classNames("ring-size-demo", `ring-size-demo--gender-${gender}`, "mb--1")}>
      <img className="ring-size-demo__hand" src={HANDS[gender]} role="presentation" alt="" />
      <img className={classNames("ring-size-demo__ring", `ring-size-demo__ring--size-${ringFaceSize || "none"}`)} src={img} role="presentation" alt="" />
    </div>
    <a href="#" className="txt--center txt--body-2 mb--1" tabIndex="0" onClick={toggleGender}><b>Switch to {otherGender} hand</b></a>
    <select aria-label="Ring face size" className="field--md" onChange={boundSetter("ringFaceSize")} value={ringFaceSize || ''}>
      <option value="" disabled>Choose a face size</option>
      <option value="11mm">Petite (11mm)</option>
      <option value="13mm">Small (13mm)</option>
      <option value="15mm">Standard (15mm)</option>
      <option value="17mm">Large (17mm)</option>
      <option value="19mm">Extra Large (19mm)</option>
      <option value="21mm">Oversized (21mm)</option>
    </select>
  </Frame>
}

export function ShoulderDesigns({cards}) {
  const {gotoNextFrame} = useContext(FlowFramesCtx)
  const {
    data: {shoulderDesigns: shoulderDesignsRaw, shoulderDesignsSkip},
    boundSetter,
  } = useContext(KeyValueCtx)

  const
    shoulderDesigns = useMemo(() => resolveValueWithCards(shoulderDesignsRaw, cards), [shoulderDesignsRaw, cards]),
    isChecked = name => shoulderDesigns === name,
    canSubmit = shoulderDesigns || shoulderDesignsSkip

  return <Frame canSubmit={canSubmit} onSubmit={gotoNextFrame}>
    <FrameHeader>Add shoulder designs?</FrameHeader>
    <p className="txt--center mb--lg">Would you like us to add “shoulder” (side) designs to your piece? We can use the same design on both sides, a different design on each side, or no shoulder designs at all.</p>
    <div className="check-mark-cards-list mb--lg">
      {cards.map(card => <CheckMarkCard
        key={card.name}
        value={card.name}
        description={card.label}
        imgs={card.imgs}
        checked={isChecked(card.name)}
        disabled={shoulderDesignsSkip}
        onClick={boundSetter("shoulderDesigns")}
      />)}
    </div>
    <NotSure field="shoulderDesignsSkip" fieldsToWipe={['shoulderDesigns']} checked={shoulderDesignsSkip}>I’m not sure yet.</NotSure>
  </Frame>
}
